import { supabase, SUPABASE_AUTH_STORAGE_KEY } from './supabase';

const DEBUG = true;

let cachedAuthUserId: string | null = null;
let cachedPublicUserId: string | null = null;

// Read the persisted auth user directly from localStorage. We DON'T use
// supabase.auth.getSession() here because that call can deadlock on the
// v2 client's internal navigator.locks lock — a single wedged getSession
// poisons every subsequent auth read across the app. AppContext already
// boots from the same key; we mirror that here so brand loading never
// blocks on the auth lock.
function readPersistedAuthUserId(): string | null {
  try {
    const raw = localStorage.getItem(SUPABASE_AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const sess = parsed?.currentSession ?? parsed;
    return sess?.user?.id ?? null;
  } catch {
    return null;
  }
}

async function getAuthUserId(): Promise<string | null> {
  if (cachedAuthUserId) return cachedAuthUserId;

  const fromStorage = readPersistedAuthUserId();
  if (fromStorage) {
    cachedAuthUserId = fromStorage;
    if (DEBUG) console.log('[brandAccess] Auth user from localStorage:', cachedAuthUserId);
    return cachedAuthUserId;
  }

  if (DEBUG) console.warn('[brandAccess] No persisted auth user — getAuthUserId returning null');
  return null;
}

async function getPublicUserId(): Promise<string | null> {
  if (cachedPublicUserId) return cachedPublicUserId;

  const authUserId = await getAuthUserId();
  if (!authUserId) return null;

  try {
    const { data, error } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', authUserId)
      .maybeSingle();

    if (error) { console.error('[brandAccess] getPublicUserId error:', error); return null; }
    cachedPublicUserId = data?.id ?? null;
    if (DEBUG) console.log('[brandAccess] Public user ID:', cachedPublicUserId);
    return cachedPublicUserId;
  } catch (err) {
    console.error('[brandAccess] getPublicUserId failed:', err);
    return null;
  }
}

export function clearBrandAccessCache() {
  cachedAuthUserId = null;
  cachedPublicUserId = null;
  if (DEBUG) console.log('[brandAccess] Cache cleared');
}

export async function getUserBrands() {
  const publicUserId = await getPublicUserId();
  if (!publicUserId) {
    if (DEBUG) console.log('[brandAccess] No public user ID, returning empty brands');
    return [];
  }

  try {
    const [ownedResult, membershipsResult, accessResult] = await Promise.all([
      supabase
        .from('brands')
        .select('*')
        .eq('owner_id', publicUserId)
        .order('name'),
      supabase
        .from('brand_team_members')
        .select('role, brands(*)')
        .eq('user_id', publicUserId),
      supabase
        .from('user_brand_access')
        .select('can_edit, brands(*)')
        .eq('user_id', publicUserId)
        .eq('can_view', true),
    ]);

    if (ownedResult.error) throw ownedResult.error;
    if (membershipsResult.error) throw membershipsResult.error;
    if (accessResult.error) console.error('[brandAccess] user_brand_access error:', accessResult.error);

    const ownedBrands = (ownedResult.data ?? []).map((b: any) => ({
      ...b,
      user_role: 'owner',
    }));

    const teamBrands = (membershipsResult.data ?? [])
      .map((m: any) => m.brands ? { ...m.brands, user_role: m.role ?? 'contributor' } : null)
      .filter(Boolean);

    // Brands shared via user_brand_access — carry can_edit flag through
    const accessBrands = (accessResult.data ?? [])
      .map((a: any) => a.brands ? {
        ...a.brands,
        user_role: a.can_edit ? 'contributor' : 'viewer',
        // flag so the context knows this brand was granted as a view-only access row
        // (used to identify passthrough parent grants)
        _access_can_edit: a.can_edit,
      } : null)
      .filter(Boolean);

    // Deduplicate — prefer higher-privilege entries (owned > team > access)
    // by building the map in ascending priority order so higher-prio overwrites
    const priorityOrder = [...accessBrands, ...teamBrands, ...ownedBrands];
    const deduped: any[] = Array.from(
      new Map(priorityOrder.map((b: any) => [b.id, b])).values()
    );

    // ── Include parent brands for any sub-brands the user directly has access to ──
    // These are tagged as `is_passthrough_parent: true` so the context can
    // exclude them from the active-brand selector for contributors/viewers.
    const subBrandEntries = deduped.filter((b: any) => b.parent_brand_id);
    const parentIds = [...new Set(
      subBrandEntries.map((b: any) => b.parent_brand_id).filter(Boolean)
    )] as string[];

    if (parentIds.length > 0) {
      const existingIds = new Set(deduped.map((b: any) => b.id));
      const missingParentIds = parentIds.filter((id) => !existingIds.has(id));

      if (missingParentIds.length > 0) {
        const { data: parentBrands, error: parentError } = await supabase
          .from('brands')
          .select('*')
          .in('id', missingParentIds)
          .eq('is_draft', false);

        if (parentError) {
          console.error('[brandAccess] Error fetching parent brands:', parentError);
        } else if (parentBrands) {
          for (const pb of parentBrands) {
            // Mark as viewer + passthrough — they can see the parent in the
            // switcher but cannot act on it (campaigns, content, etc.)
            deduped.push({ ...pb, user_role: 'viewer', is_passthrough_parent: true });
          }
        }
      }
    }

    // Also flag any top-level brand already in the list where the user only
    // has a view-only access row (can_edit: false) AND is not an owner/team-member.
    // These are parent brands shared view-only so sub-brand context works.
    for (const b of deduped) {
      if (
        !b.is_passthrough_parent &&
        b.user_role === 'viewer' &&
        !b.is_sub_brand &&
        !b.parent_brand_id
      ) {
        // Check whether any sub-brand in the list has this as its parent
        const hasSubBrandAccess = deduped.some(
          (s: any) => s.parent_brand_id === b.id && s.user_role !== 'viewer'
        );
        if (hasSubBrandAccess) {
          b.is_passthrough_parent = true;
        }
      }
    }

    if (DEBUG) console.log('[brandAccess] getUserBrands result:', deduped.length, 'brands');
    return deduped;
  } catch (err) {
    console.error('[brandAccess] getUserBrands failed:', err);
    return [];
  }
}

export async function getUserBrandIds(): Promise<string[]> {
  const brands = await getUserBrands();
  return brands.map((b: any) => b.id);
}

export async function getUserBrandRole(brandId: string): Promise<string | null> {
  const publicUserId = await getPublicUserId();
  if (!publicUserId) return null;

  try {
    const [brandResult, membershipResult] = await Promise.all([
      supabase
        .from('brands')
        .select('owner_id')
        .eq('id', brandId)
        .maybeSingle(),
      supabase
        .from('brand_team_members')
        .select('role')
        .eq('brand_id', brandId)
        .eq('user_id', publicUserId)
        .maybeSingle(),
    ]);

    if (brandResult.error) throw brandResult.error;
    if (membershipResult.error) throw membershipResult.error;

    if (brandResult.data?.owner_id === publicUserId) return 'brand_owner';
    return membershipResult.data?.role || null;
  } catch (err) {
    console.error('[brandAccess] getUserBrandRole failed:', err);
    return null;
  }
}

/**
 * Returns the effective access level for the current user on a specific brand.
 * Use this to gate campaign/content create actions in the dashboard.
 */
export async function getEffectiveBrandAccess(brandId: string): Promise<{
  canEdit: boolean;
  canView: boolean;
  role: string | null;
}> {
  const publicUserId = await getPublicUserId();
  if (!publicUserId) return { canEdit: false, canView: false, role: null };

  try {
    const [membershipResult, accessResult, brandResult] = await Promise.all([
      supabase
        .from('brand_team_members')
        .select('role')
        .eq('brand_id', brandId)
        .eq('user_id', publicUserId)
        .maybeSingle(),
      supabase
        .from('user_brand_access')
        .select('can_edit, can_view')
        .eq('brand_id', brandId)
        .eq('user_id', publicUserId)
        .maybeSingle(),
      supabase
        .from('brands')
        .select('owner_id')
        .eq('id', brandId)
        .maybeSingle(),
    ]);

    const isOwner = brandResult.data?.owner_id === publicUserId;
    const ownerRoles = ['workspace_owner', 'brand_owner', 'admin'];
    const roleAllowsEdit = ownerRoles.includes(membershipResult.data?.role ?? '');

    const canEdit = isOwner || roleAllowsEdit || accessResult.data?.can_edit === true;
    const canView = canEdit || accessResult.data?.can_view === true || !!membershipResult.data;

    return {
      canEdit,
      canView,
      role: membershipResult.data?.role ?? (isOwner ? 'brand_owner' : null),
    };
  } catch (err) {
    console.error('[brandAccess] getEffectiveBrandAccess failed:', err);
    return { canEdit: false, canView: false, role: null };
  }
}

export async function isWorkspaceOwner(): Promise<boolean> {
  const authUserId = await getAuthUserId();
  if (!authUserId) return false;

  try {
    const { data, error } = await supabase
      .from('users')
      .select('role')
      .eq('auth_id', authUserId)
      .maybeSingle();

    if (error) throw error;
    const ok = data?.role === 'workspace_owner';
    if (DEBUG) console.log('[brandAccess] isWorkspaceOwner:', ok);
    return ok;
  } catch (err) {
    console.error('[brandAccess] isWorkspaceOwner failed:', err);
    return false;
  }
}

export async function canManageUsers(): Promise<boolean> {
  return await isWorkspaceOwner();
}

export async function getBrandConnections(brandId: string, parentBrandId?: string | null) {
  try {
    const { data: own, error } = await supabase
      .from('social_connections')
      .select('*')
      .eq('brand_id', brandId);

    if (error) throw error;
    if (own && own.length > 0) return own;

    if (parentBrandId) {
      const { data: parent, error: parentError } = await supabase
        .from('social_connections')
        .select('*')
        .eq('brand_id', parentBrandId);

      if (parentError) throw parentError;
      return parent ?? [];
    }

    return [];
  } catch (err) {
    console.error('[brandAccess] getBrandConnections failed:', err);
    return [];
  }
}